import { useQuery } from '@tanstack/react-query';
import { supabase } from './supabase';
import { useSession } from './session';

export interface NavCounts {
  clients: number;
  projects: number;
  suppliers: number;
  subcontractors: number;
  materials: number;
  bankAccounts: number;
  staff: number;
}

/**
 * How many records live in each directory, shown as a small badge beside its
 * nav item — matching the prototype this app was built from.
 *
 * Each is a head-only count: `{ count: 'exact', head: true }` asks Postgres
 * for a number, not rows, so this never pulls anyone's actual client list or
 * supplier list across the wire just to badge the sidebar.
 *
 * Kept apart from Approvals' own count (`lib/approvals.ts`), which is a
 * different thing wearing a similar badge: that one says "something needs
 * you", refetches every 60 seconds, and turns orange for it. These say "how
 * big is this list", change only when someone adds or removes a record, and
 * stay the same neutral grey as the rest of the sidebar.
 */
export function useNavCounts() {
  const actor = useSession((s) => s.actor);

  return useQuery({
    queryKey: ['nav-counts', actor?.companyId ?? ''],
    enabled: !!actor,
    staleTime: 60_000,
    queryFn: async (): Promise<NavCounts> => {
      // Written out rather than a generic helper taking a table-name string:
      // the generated Supabase client only accepts literal table names, not
      // ones assembled or passed at runtime — the same restriction hit while
      // building activity.ts.
      const [clients, projects, suppliers, subcontractors, materials, bankAccounts, staff] = await Promise.all([
        supabase.from('clients').select('id', { count: 'exact', head: true }).is('deleted_at', null),
        supabase.from('projects').select('id', { count: 'exact', head: true }).is('deleted_at', null),
        supabase.from('suppliers').select('id', { count: 'exact', head: true }).is('deleted_at', null),
        supabase.from('subcontractors').select('id', { count: 'exact', head: true }).is('deleted_at', null),
        supabase.from('materials').select('id', { count: 'exact', head: true }).is('deleted_at', null),
        supabase.from('bank_accounts').select('id', { count: 'exact', head: true }).is('deleted_at', null),
        // profiles has no soft delete — a staff login is deactivated, not deleted.
        supabase.from('profiles').select('id', { count: 'exact', head: true }).eq('is_active', true),
      ]);

      for (const r of [clients, projects, suppliers, subcontractors, materials, bankAccounts, staff]) {
        if (r.error) throw new Error(r.error.message);
      }

      return {
        clients: clients.count ?? 0,
        projects: projects.count ?? 0,
        suppliers: suppliers.count ?? 0,
        subcontractors: subcontractors.count ?? 0,
        materials: materials.count ?? 0,
        bankAccounts: bankAccounts.count ?? 0,
        staff: staff.count ?? 0,
      };
    },
  });
}
